import React, { useState } from "react";
import { Pencil, Trash2, X, ChevronLeft, ChevronRight, Plus } from "lucide-react";

export default function SocialImpact() {
  const [data, setData] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    category: "",
    impact: "",
    year: ""
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(data.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDelete = (id) => {
    setData(data.filter((item) => item.id !== id));
    setShowModal(false);
    
    // Adjust current page if needed
    if (currentPage > 1 && currentItems.length === 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddImpact = (e) => {
    e.preventDefault();
    if (formData.category && formData.impact && formData.year) {
      const newImpact = {
        id: Date.now(), // Using timestamp as unique ID
        ...formData
      };
      setData(prev => [...prev, newImpact]);
      setFormData({
        category: "",
        impact: "",
        year: ""
      });
      setShowForm(false);
      // Go to last page when new item is added
      setCurrentPage(Math.ceil((data.length + 1) / itemsPerPage));
    }
  };

  return (
    <div className="p-4 bg-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold">Social Impact</h3>
        <button
          onClick={() => setShowForm(true)}
          className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded-full border border-blue-500"
          title="Add Social Impact"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Add Impact Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-4 w-full max-w-xs border border-gray-700">
            <div className="flex justify-between items-center mb-3 border-b border-gray-700 pb-2">
              <h3 className="font-medium">Add Social Impact</h3>
              <button 
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddImpact}>
              <div className="mb-3">
                <label className="block text-gray-300 mb-1 text-sm">Category</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="block text-gray-300 mb-1 text-sm">Impact</label>
                <input
                  type="text"
                  name="impact"
                  value={formData.impact}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-300 mb-1 text-sm">Year</label>
                <input
                  type="text"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  required
                />
              </div>
              <div className="flex justify-end space-x-2 border-t border-gray-700 pt-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-700 rounded border border-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 rounded border border-blue-500"
                >
                  Add Impact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="text-sm border border-gray-700 rounded-lg overflow-hidden">
        {data && data.length > 0 ? (
          <>
            <table className="w-full">
              <thead>
                <tr className="bg-gray-750 text-gray-300 border-b border-gray-700">
                  <th className="px-3 py-2 text-left border-r border-gray-700">Category</th>
                  <th className="px-3 py-2 text-left border-r border-gray-700">Impact</th>
                  <th className="px-3 py-2 text-left border-r border-gray-700">Year</th>
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => (
                  <tr key={item.id} className="border-b border-gray-700 hover:bg-gray-750 last:border-b-0">
                    <td className="px-3 py-2 border-r border-gray-700 text-left">{item.category}</td>
                    <td className="px-3 py-2 border-r border-gray-700 text-left">{item.impact}</td>
                    <td className="px-3 py-2 border-r border-gray-700 text-left">{item.year}</td>
                    <td className="px-3 py-2">
                      <div className="flex space-x-2">
                        <button className="text-blue-400 hover:text-blue-300">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          className="text-red-400 hover:text-red-300"
                          onClick={() => {
                            setDeleteId(item.id);
                            setShowModal(true);
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination controls */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-750 border-t border-gray-700">
              <div className="text-xs text-gray-400">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, data.length)} of {data.length}
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-1.5 rounded border ${currentPage === 1 ? 'bg-gray-700 border-gray-600' : 'bg-gray-600 border-gray-500 hover:bg-gray-550'}`}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`p-1.5 px-2.5 rounded border ${currentPage === number ? 'bg-blue-600 border-blue-500' : 'bg-gray-600 border-gray-500 hover:bg-gray-550'}`}
                  >
                    {number}
                  </button>
                ))}
                
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-1.5 rounded border ${currentPage === totalPages ? 'bg-gray-700 border-gray-600' : 'bg-gray-600 border-gray-500 hover:bg-gray-550'}`}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4 text-gray-400">
            <button
              onClick={() => setShowForm(true)}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              + Add Social Impact
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-4 w-full max-w-xs border border-gray-700">
            <div className="flex justify-between items-center mb-3 border-b border-gray-700 pb-2">
              <h3 className="font-medium">Confirm Deletion</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-300 mb-4">
              Delete this social impact detail?
            </p>
            <div className="flex justify-end space-x-2 border-t border-gray-700 pt-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-700 rounded border border-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 rounded flex items-center border border-red-500"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// import React, { useState } from "react";
// import { Pencil, Trash2, X, ChevronLeft, ChevronRight, Plus } from "lucide-react";

// export default function SocialImpact() {
//   const [data, setData] = useState([]);
//   const [deleteId, setDeleteId] = useState(null);
//   const [showModal, setShowModal] = useState(false);
//   const [showForm, setShowForm] = useState(false);
//   const [formData, setFormData] = useState({
//     category: "",
//     impact: "",
//     year: ""
//   });
  
//   // Pagination state
//   const [currentPage, setCurrentPage] = useState(1);
//   const [itemsPerPage] = useState(5);

//   // Pagination logic
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(data.length / itemsPerPage);

//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   const handleDelete = (id) => {
//     setData(data.filter((item) => item.id !== id));
//     setShowModal(false);
    
//     // Adjust current page if needed
//     if (currentPage > 1 && currentItems.length === 1) {
//       setCurrentPage(currentPage - 1);
//     }
//   };

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }));
//   };

//   const handleAddImpact = (e) => {
//     e.preventDefault();
//     if (formData.category && formData.impact && formData.year) {
//       const newImpact = {
//         id: Date.now(), // Using timestamp as unique ID
//         ...formData
//       };
//       setData(prev => [...prev, newImpact]);
//       setFormData({
//         category: "",
//         impact: "",
//         year: ""
//       });
//       setShowForm(false);
//       // Go to last page when new item is added
//       setCurrentPage(Math.ceil((data.length + 1) / itemsPerPage));
//     }
//   };

//   return (
//     <div className="p-4 text-white">
//       <div className="relative flex items-center mb-4">
//         <h2 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
//           Social Impact
//         </h2>
//         <button
//           onClick={() => setShowForm(true)}
//           className="ml-auto bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//         >
//           <Plus className="w-5 h-5" />
//         </button>
//       </div>

//       {/* Add Impact Form Modal */}
//       {showForm && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold">Add Social Impact</h3>
//               <button 
//                 onClick={() => setShowForm(false)}
//                 className="text-gray-400 hover:text-white"
//               >
//                 <X className="w-6 h-6" />
//               </button>
//             </div>
//             <form onSubmit={handleAddImpact}>
//               <div className="mb-4">
//                 <label className="block text-gray-300 mb-2">Category</label>
//                 <input
//                   type="text"
//                   name="category"
//                   value={formData.category}
//                   onChange={handleInputChange}
//                   className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
//                   required
//                 />
//               </div>
//               <div className="mb-4">
//                 <label className="block text-gray-300 mb-2">Impact</label>
//                 <input
//                   type="text"
//                   name="impact"
//                   value={formData.impact}
//                   onChange={handleInputChange}
//                   className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
//                   required
//                 />
//               </div>
//               <div className="mb-6">
//                 <label className="block text-gray-300 mb-2">Year</label>
//                 <input
//                   type="text"
//                   name="year"
//                   value={formData.year}
//                   onChange={handleInputChange}
//                   className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
//                   required
//                 />
//               </div>
//               <div className="flex justify-end space-x-4">
//                 <button
//                   type="button"
//                   onClick={() => setShowForm(false)}
//                   className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors border border-blue-700"
//                 >
//                   Add Impact
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}

//       <div className="flex flex-col items-center">
//         {data && data.length > 0 ? (
//           <>
//             <div className="overflow-x-auto w-full">
//               <table className="border border-gray-600 text-sm w-full mb-4">
//                 <thead className="bg-gray-800 text-gray-200">
//                   <tr>
//                     <th className="border border-gray-600 px-4 py-2 text-left">Category</th>
//                     <th className="border border-gray-600 px-4 py-2 text-left">Impact</th>
//                     <th className="border border-gray-600 px-4 py-2 text-left">Year</th>
//                     <th className="border border-gray-600 px-4 py-2 text-left">Actions</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {currentItems.map((item) => (
//                     <tr key={item.id} className="hover:bg-gray-700">
//                       <td className="border border-gray-600 px-4 py-2">{item.category}</td>
//                       <td className="border border-gray-600 px-4 py-2">{item.impact}</td>
//                       <td className="border border-gray-600 px-4 py-2">{item.year}</td>
//                       <td className="border border-gray-600 px-4 py-2">
//                         <div className="flex justify-center space-x-4">
//                           <button className="text-blue-500 hover:text-blue-700">
//                             <Pencil className="w-4 h-4" />
//                           </button>
//                           <button
//                             className="text-red-500 hover:text-red-700"
//                             onClick={() => {
//                               setDeleteId(item.id);
//                               setShowModal(true);
//                             }}
//                           >
//                             <Trash2 className="w-4 h-4" />
//                           </button>
//                         </div>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>

//             {/* Pagination controls */}
//             <div className="flex items-center justify-between w-full mt-4">
//               <div className="text-sm text-gray-400">
//                 Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, data.length)} of {data.length} entries
//               </div>
//               <div className="flex space-x-2">
//                 <button
//                   onClick={() => paginate(currentPage - 1)}
//                   disabled={currentPage === 1}
//                   className={`p-2 rounded-md ${currentPage === 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//                 >
//                   <ChevronLeft className="w-4 h-4" />
//                 </button>
                
//                 {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
//                   <button
//                     key={number}
//                     onClick={() => paginate(number)}
//                     className={`p-2 px-3 rounded-md ${currentPage === number ? 'bg-blue-500 text-white' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//                   >
//                     {number}
//                   </button>
//                 ))}
                
//                 <button
//                   onClick={() => paginate(currentPage + 1)}
//                   disabled={currentPage === totalPages}
//                   className={`p-2 rounded-md ${currentPage === totalPages ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//                 >
//                   <ChevronRight className="w-4 h-4" />
//                 </button>
//               </div>
//             </div>
//           </>
//         ) : (
//           <div className="text-center py-8 text-gray-400">
//             No social impact details added yet. Click the + button to add some.
//           </div>
//         )}
//       </div>

//       {/* Delete Confirmation Modal */}
//       {showModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div
//             className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all border border-gray-700"
//           >
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//               <button
//                 onClick={() => setShowModal(false)}
//                 className="text-gray-400 hover:text-white">
//                 <X className="w-6 h-6" />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">
//               Are you sure you want to delete this social impact detail? This action cannot be undone.
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={() => setShowModal(false)}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={() => handleDelete(deleteId)}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
//               >
//                 <Trash2 className="w-4 h-4 mr-2" />
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }
// import React, { useState } from "react";
// import { Pencil, Trash2, X } from "lucide-react";

// const dummyData = [
//   { id: 1, category: "Education", impact: "Built 2 schools", year: "2023" },
//   { id: 2, category: "Healthcare", impact: "Free medical camp", year: "2024" },
// ];

// export default function SocialImpact() {
//   const [data, setData] = useState(dummyData);
//   const [deleteId, setDeleteId] = useState(null);
//   const [showModal, setShowModal] = useState(false);

//   const handleDelete = (id) => {
//     setData(data.filter((item) => item.id !== id));
//     setShowModal(false);
//   };

//   return (
//     <div className="p-4 bg-transparent rounded-2xl shadow-md">
//       <h2 className="text-xl font-semibold text-center mb-4">Social Impact</h2>
//       <div className="overflow-x-auto">
//         <table className="w-full text-sm text-left border border-gray-800">
//           <thead className="bg-gray-800">
//             <tr>
//               <th className="border border-gray-600 px-4 py-2 text-left">Category</th>
//               <th className="border border-gray-600 px-4 py-2 text-left">Impact</th>
//               <th className="border border-gray-600 px-4 py-2 text-left">Year</th>
//               <th className="border border-gray-600 px-4 py-2 text-left ">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {data.map((item) => (
//               <tr key={item.id} className="hover:bg-gray-700">
//                 <td className="border border-gray-600 px-4 py-2 text-left">{item.category}</td>
//                 <td className="border border-gray-600 px-4 py-2 text-left">{item.impact}</td>
//                 <td className="border border-gray-600 px-4 py-2 text-left">{item.year}</td>
//                 <td className="border border-gray-600 px-4 py-2 text-left">
//                   <div className="flex justify-center space-x-2">
//                     <button className="text-blue-600 hover:text-blue-800">
//                       <Pencil size={16} />
//                     </button>
//                     <button
//                       className="text-red-600 hover:text-red-800"
//                       onClick={() => {
//                         setDeleteId(item.id);
//                         setShowModal(true);
//                       }}
//                     >
//                       <Trash2 size={16} />
//                     </button>
//                   </div>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* Delete Confirmation Modal */}
//       {showModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div
//             className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all border border-gray-700"
//           >
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white text-center">Confirm Deletion</h3>
//               <button
//                 onClick={() => setShowModal(false)}
//                 className="text-gray-400 hover:text-white">
//                 <X className="w-6 h-6" />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">
//               Are you sure you want to delete this transport detail? This action cannot be undone.
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={() => setShowModal(false)}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={() => handleDelete(deleteId)}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
//               >
//                 <Trash2 className="w-4 h-4 mr-2" />
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }
